import React from "react";
import styled from "styled-components";

import { HTMLEditor } from "../../../components/course-author/html-editor/Editor.container";
import { HTMLRenderer } from "../../../components/course-author/html-editor/Renderer";
import Checkbox from "../../../components/pages/CourseAuthor/Checkbox";
import LocalizedMessage from "../../../components/pages/CourseAuthor/SettingsTab/LocalizedMessage";
import { dependencies } from "../../../dependencies";
import { GetLocalizedMessage } from "../../../reducers/course-author/types";
import { PreferredLocale } from "../../../utils/locales";
import {
  CheckboxDescription,
  FieldSet,
  FieldSetToggleDisplay,
  FooterNote,
  Input,
  SettingsInputItem,
  StyledSelect,
} from "../../Common/Components/FormComponents";
import { Section } from "../../Common/Styles/containers";
import { IScormPackage } from "../../types/common";
import BrandingAndReview from "./BrandingAndReview";
import GoalMode from "./GoalMode";
import LockPackage from "./LockPackage";

const { api } = dependencies;

const ExpetedNumUsersInput = styled(Input)`
  background-color: ${({ theme }) => theme.colors.lightgrey3};
  border: none;
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.lightgrey2};
  font-size: ${({ theme }) => theme.fontSizes.regular};
`;

const ExpectedNumUsersFieldSet = styled(FieldSet)`
  margin: 2rem 0.2rem;
`;

const Note = styled.p`
  margin: 2rem 0;
  font-size: ${({ theme }) => theme.fontSizes.smallText};
  font-weight: 600;
  font-style: italic;
`;

const CustomMessageContainer = styled.div`
  margin: 2rem 0 3rem 0;
`;

interface ISettingsProps {
  accountId: string;
  currentLocale: PreferredLocale;
  modifyInProgressExitText: (htmlContent: string) => void;
  modifyCompletedExitText: (htmlContent: string) => void;
  modifyUseStrictMode: (bool: boolean) => void;
  scormPackage: IScormPackage;
  toggleUseCustomCompletionExitText: (bool: boolean) => void;
  toggleAllowCourseReview: (bool: boolean) => void;
  toggleIncludeBranding: (bool: boolean) => void;
  toggleLockPackage: (bool: boolean) => void;
  toggleUseCustomInProgressExitText: (bool: boolean) => void;
  updateNumExpectedUsers: (numOfUsers: number) => void;
  updateScormPackageState: (option: string) => void;
}

interface ICustomMessage {
  currentLocale: PreferredLocale;
  customMessage: string;
  setCustomMessage: (message: string) => void;
  footerNote: string;
  getContent: () => string;
  toggleUseCustom: (bool: boolean) => void;
  getMessage: GetLocalizedMessage;
  useCustom: boolean;
}

const CustomMessage: React.FC<ICustomMessage> = props => {
  const {
    currentLocale,
    footerNote,
    customMessage,
    getContent,
    setCustomMessage,
    useCustom,
    toggleUseCustom,
    getMessage,
  } = props;
  const [edit, setEdit] = React.useState(false);
  const toggleEdit = () => setEdit(!edit);
  const handleToggleUseCustom = (ev: React.MouseEvent<HTMLInputElement>) => {
    const newValue = (ev.target as HTMLInputElement).checked;
    if (newValue !== useCustom) {
      toggleUseCustom(newValue);
    }
  };
  const handleSetCustomInProgressMessage = (message: string) => {
    if (message !== getContent()) {
      setCustomMessage(message);
      toggleEdit();
    }
  };

  const containerRef = React.useRef() as React.RefObject<HTMLDivElement>;

  return (
    <CustomMessageContainer>
      <SettingsInputItem>
        <label>
          <Checkbox defaultChecked={useCustom} onChange={handleToggleUseCustom} />
          <CheckboxDescription>Custom "{customMessage}" Message</CheckboxDescription>
        </label>
      </SettingsInputItem>

      <p>
        <FooterNote>{footerNote}</FooterNote>
      </p>

      {useCustom ? (
        <div ref={containerRef}>
          {!edit && (
            <FieldSetToggleDisplay
              legend={`Custom '${customMessage}' Message:`}
              onClick={toggleEdit}
              shouldDisplay={!edit}
            >
              <HTMLRenderer source={getContent()} placeholder={`Type here to set Custom ${customMessage} Message`} />
            </FieldSetToggleDisplay>
          )}
          {edit && (
            <HTMLEditor
              getContent={getContent}
              setContent={handleSetCustomInProgressMessage}
              currentLocale={currentLocale}
              shouldAutoFocus={false}
              containerRef={containerRef}
              toggleEdit={toggleEdit}
            />
          )}
        </div>
      ) : (
        <LocalizedMessage
          locale={currentLocale}
          getMessage={getMessage}
          title={`Default "${customMessage}" Message:`}
        />
      )}
    </CustomMessageContainer>
  );
};

const Settings: React.FC<ISettingsProps> = props => {
  const { scormPackage } = props;

  const expectedNumOfUsersName = "expected-num-of-users";
  const expectedNumOfUsersRef: React.RefObject<HTMLInputElement> = React.createRef();

  const updateNumExpectedUsers = (ev: React.FocusEvent<HTMLInputElement>) => {
    const newValue = Math.floor(Number(ev.target.value));
    if (newValue !== scormPackage.expectedNumUsers) {
      props.updateNumExpectedUsers(newValue);
    }
  };

  const getScormExitPageBodyCourseInProgress = api.messages.getScormExitPageBodyCourseInProgress(props.accountId);
  const getScormExitPageBodyCourseCompleted = api.messages.getScormExitPageBodyCourseCompleted(props.accountId);
  const updateScormPackageState = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = ev.target.value;
    if (newValue !== scormPackage.state) {
      props.updateScormPackageState(newValue);
    }
  };
  const getInProgressMessage = () => {
    return scormPackage.inProgressExitText.elems.find(({ locale }) => locale === props.currentLocale)?.value.text ?? "";
  };

  const getCompletionMessage = () => {
    return scormPackage.completedExitText.elems.find(({ locale }) => locale === props.currentLocale)?.value.text ?? "";
  };

  const toggleAllowCourseReview = (ev: React.MouseEvent<HTMLInputElement>) => {
    const newValue = (ev.target as HTMLInputElement).checked;
    if (newValue !== scormPackage.allowCourseReview) {
      props.toggleAllowCourseReview(newValue);
    }
  };

  const toggleIncludeBranding = (ev: React.MouseEvent<HTMLInputElement>) => {
    const newValue = (ev.target as HTMLInputElement).checked;
    if (newValue !== scormPackage.includeBranding) {
      props.toggleIncludeBranding(newValue);
    }
  };

  const modifyUseStrictMode = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = (ev.target as HTMLSelectElement).value;
    const toBoolean = newValue === "1";
    if (toBoolean !== scormPackage.useStrictMode) {
      props.modifyUseStrictMode(toBoolean);
    }
  };

  const toggleLockPackage = (ev: React.MouseEvent<HTMLInputElement>) => {
    const newValue = (ev.target as HTMLInputElement).checked;
    if (newValue !== scormPackage.locked) {
      props.toggleLockPackage(newValue);
    }
  };

  return (
    <div>
      <Section>
        <ExpectedNumUsersFieldSet>
          <legend>Expected number of users</legend>
          <ExpetedNumUsersInput
            name={expectedNumOfUsersName}
            ref={expectedNumOfUsersRef}
            placeholder={"Type here"}
            type="number"
            step={1}
            min={0}
            defaultValue={`${scormPackage.expectedNumUsers || ""}`}
            onBlur={updateNumExpectedUsers}
          />
        </ExpectedNumUsersFieldSet>
        <Note>
          The number of users which are expected to take this course in total. Leave this option blank to indicate that
          this package will recieve an unknown number of users.
        </Note>
        <h4>Package State</h4>
        <StyledSelect defaultValue={scormPackage.state} onChange={updateScormPackageState}>
          <option value={"open"}>Open</option>
          <option value={"closed"}>Closed</option>
        </StyledSelect>
        <Note>Setting this option to "closed" will prevent users from logging in using this package.</Note>
      </Section>

      <Section>
        <CustomMessage
          currentLocale={props.currentLocale}
          setCustomMessage={props.modifyInProgressExitText}
          customMessage={"In Progress"}
          footerNote={`
            This is the message which a learner will see if they exit the course
            before completing it
          `}
          getContent={getInProgressMessage}
          toggleUseCustom={props.toggleUseCustomInProgressExitText}
          getMessage={getScormExitPageBodyCourseInProgress}
          useCustom={scormPackage.useCustomInProgressExitText}
        />

        <CustomMessage
          currentLocale={props.currentLocale}
          customMessage={"Completion"}
          setCustomMessage={props.modifyCompletedExitText}
          footerNote={`
            This is the message which a learner will see if they exit the
            course, having completed it.
          `}
          getContent={getCompletionMessage}
          toggleUseCustom={props.toggleUseCustomCompletionExitText}
          getMessage={getScormExitPageBodyCourseCompleted}
          useCustom={scormPackage.useCustomCompletedExitText}
        />
      </Section>
      <GoalMode useStrictMode={scormPackage.useStrictMode} modifyUseStrictMode={modifyUseStrictMode} />
      <BrandingAndReview
        allowCourseReview={scormPackage.allowCourseReview}
        includeBranding={scormPackage.includeBranding}
        toggleAllowCourseReview={toggleAllowCourseReview}
        toggleIncludeBranding={toggleIncludeBranding}
      />
      <LockPackage lockPackage={scormPackage.locked} toggleLockPackage={toggleLockPackage} />
    </div>
  );
};

export default Settings;
